// The stage ladder shared with the family's app (apps/mobile/src/progress/stages):
// Not started → Exploring → With a little help → No game help → Steady → In new
// places. A step's stage NEVER goes down, so a stage at a given week is the
// highest stage whose (effective) date is on or before that week.

import type { IsoDate, SkillStepState, StepStage } from "../domain/types";
import { mondayOf } from "./weeks";

export const STAGES: readonly StepStage[] = ["exploring", "help", "own", "steady", "places"];

/** 0-based rank of a stage; "Not started" is -1. */
export function stageRank(stage: StepStage | null | undefined): number {
  return stage ? STAGES.indexOf(stage) : -1;
}

/** Own-or-above: the steps done with no game help (the summary's growth measure). */
export function isOwnOrAbove(stage: StepStage | null | undefined): boolean {
  return stageRank(stage) >= stageRank("own");
}

function rawDates(s: SkillStepState): (IsoDate | null)[] {
  return [s.exploreOn, s.helpOn, s.ownOn, s.steadyOn, s.placesOn];
}

/**
 * The date each stage was reached, made consistent:
 *   • the top stage is the higher of `stage` and the highest stage with a date
 *     (a stale `stage` column can never pull a step down);
 *   • a stage with no date of its own takes the next higher stage's date, and the
 *     top stage with no date at all takes the row's update week;
 *   • a lower stage is never later than a higher one.
 * Index i corresponds to {@link STAGES}[i]; null = not reached.
 */
export function effectiveStageDates(s: SkillStepState): (IsoDate | null)[] {
  const raw = rawDates(s);
  let top = stageRank(s.stage);
  raw.forEach((d, i) => {
    if (d && i > top) top = i;
  });
  const out: (IsoDate | null)[] = STAGES.map(() => null);
  if (top < 0) return out;
  let ceiling: IsoDate = raw[top] ?? mondayOf((s.updatedAt || s.lastPractisedOn || "1970-01-05").slice(0, 10));
  for (let i = top; i >= 0; i--) {
    const own = raw[i];
    const d = own && own < ceiling ? own : ceiling;
    out[i] = d;
    ceiling = d;
  }
  return out;
}

/** The step's stage on `date` (inclusive), or null when not yet started. */
export function stageOn(s: SkillStepState, date: IsoDate): StepStage | null {
  const dates = effectiveStageDates(s);
  let found: StepStage | null = null;
  dates.forEach((d, i) => {
    if (d && d <= date) found = STAGES[i];
  });
  return found;
}

/** The step's current stage (the top of its consistent ladder). */
export function currentStage(s: SkillStepState): StepStage | null {
  const dates = effectiveStageDates(s);
  let found: StepStage | null = null;
  dates.forEach((d, i) => {
    if (d) found = STAGES[i];
  });
  return found;
}

/** The earliest date this step has, or null. */
export function firstDate(s: SkillStepState): IsoDate | null {
  return effectiveStageDates(s).find((d) => d !== null) ?? null;
}
